import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { claimLaborCo, laborCoLogin, getMe } from '../lib/api';
import { Motif } from '../components/ui/Motif';

/** Friendly copy per phone-SSO failure code (the callback 302s back here with ?sso_error=). */
const PHONE_SSO_ERRORS: Record<string, string> = {
  not_registered: '该手机号不在 WTL 的劳务名录里 — 请联系 WTL 把您的手机号加到贵司资料后再试。 / This phone number isn\'t on WTL\'s supplier roster yet — ask WTL to add it to your company profile.',
  ambiguous: '该手机号对应了多家公司，请联系 WTL 核对贵司联系方式。 / This number matches more than one company — ask WTL to fix the roster.',
  claims: '登录校验未通过，请重新用手机号验证。 / Verification failed — try the phone sign-in again.',
  state: '登录会话已过期，请重新开始。 / The sign-in session expired — start again.',
  exchange: '登录服务暂时不可用，请稍后再试。 / Sign-in service unavailable — try again shortly.',
  verify: '登录校验失败，请重试。 / Verification failed — please retry.',
  disabled: '手机号登录暂未开通。 / Phone sign-in isn\'t enabled yet.',
  server: '登录出错了，请重试。 / Something went wrong — please retry.',
};

/** Phase 3 · labor-company login. First time: paste the single-use claim token an admin sent you.
 *  Returning: log in by unified credit code (dev). On success → the portal. */
export default function PortalLogin() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [token, setToken] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: getMe });
  const [searchParams] = useSearchParams();
  const ssoError = searchParams.get('sso_error');

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setErr(null);
    try { await fn(); await qc.invalidateQueries(); nav('/portal'); }
    catch (e: any) { setErr(e?.body?.error ?? e?.message ?? null); }
    finally { setBusy(false); }
  }

  return (
    <div className="portal" style={{ maxWidth: 460, margin: '0 auto' }}>
      <div className="enter-pop" style={{ textAlign: 'center', marginTop: 14 }}>
        <Motif variant="welcome" size={72} className="motif-center" />
        <h2 style={{ marginTop: 4, marginBottom: 2 }}>欢迎来到 WTL 投标平台 / Welcome to WTL bidding</h2>
        <p className="muted small" style={{ marginTop: 2 }}>劳务公司专用入口 / The labor-company entrance</p>
      </div>

      {ssoError && (
        <div className="card enter-pop small" style={{ borderColor: 'var(--bad)', background: 'var(--bad-soft)' }}>
          {PHONE_SSO_ERRORS[ssoError] ?? PHONE_SSO_ERRORS.server}
        </div>
      )}

      {me?.phone_sso_enabled && (
        <div className="card">
          <strong className="small">用手机号登录 / Sign in with your phone</strong>
          <div className="small muted" style={{ margin: '4px 0 8px' }}>用贵司在 WTL 登记的手机号，接收短信验证码即可进入。 / Use the mobile number WTL has on file — verify with an SMS code.</div>
          <a href="/api/auth/phone-sso/login" className="btn-primary" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            📱 手机号 + 短信验证码登录 / Phone + SMS sign-in
          </a>
        </div>
      )}

      {err != null && (
        <div className="card enter-pop small" style={{ borderColor: 'var(--bad)', background: 'var(--bad-soft)' }}>
          没登上，再试一次？令牌或信用代码不对的话，请联系 WTL 重新发一个。 / Didn't work — double-check and retry, or ask WTL to resend.
          <div className="muted" style={{ marginTop: 4 }}>{err}</div>
        </div>
      )}

      <div className="card">
        <strong className="small">第一次来？ / First time here?</strong>
        <div className="small muted" style={{ margin: '4px 0 8px' }}>把 WTL 发您的「认领令牌」粘到这里就行。 / Just paste the claim token WTL sent you.</div>
        <div className="row-wrap" style={{ gap: 6 }}>
          <input className="grow" style={{ minWidth: 180 }} value={token} onChange={(e) => setToken(e.target.value)} placeholder="认领令牌 / claim token" />
          <button className="btn-primary" disabled={busy || !token.trim()} onClick={() => run(() => claimLaborCo(token.trim()))}>{busy ? '进入中… / Entering…' : '认领进入 / Claim & enter'}</button>
        </div>

        <div className="hairline" style={{ margin: '14px 0 10px' }} />

        <strong className="small">来过了？ / Been here before?</strong>
        <div className="small muted" style={{ margin: '4px 0 8px' }}>用统一社会信用代码登录。 / Sign in with your unified credit code.</div>
        <div className="row-wrap" style={{ gap: 6 }}>
          <input className="grow" style={{ minWidth: 180 }} value={code} onChange={(e) => setCode(e.target.value)} placeholder="统一社会信用代码 / unified credit code" />
          <button disabled={busy || !code.trim()} onClick={() => run(() => laborCoLogin(code.trim()))}>登录 / Sign in</button>
        </div>
      </div>
    </div>
  );
}
